import { Button, Link } from '../../../../../widgets/buttons';
import { FormControl } from '../../../../../widgets/formControl';
import { Icon } from '../../../../../widgets/icon';
import { PageFooterButtons } from '../../../../../widgets/headers';
import { PageHeader } from '../../../../../widgets/pageHeader';
import { showNotification } from '../../../../../widgets/notificationToaster';
import { QuestionDialog, useDialogManager } from '../../../../../widgets/dialog';
import { ContainerBase } from '../../../../../widgets/layouts';
import { debounce } from 'lodash';
import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Section } from 'src/components/widgets/section';
import { InputWithValidation } from 'src/shared/globals';
import { cartesianProduct, T, uuid, validateInputSecurity } from 'src/shared/helpers';
import AiOutlinePlus from 'src/shared/icons/plus-solid.svg';
import TrashIcon from 'src/shared/icons/trash-alt.svg';
import { validateRequired } from 'src/shared/validation';
import styled from 'styled-components';
import { addNewColorAction, editColorNameAndAttributesAction, removeColorAction, useProductConfigurationState } from '../../../state';
import SelectedOptionValue from '../../../state/selectedOptionValue';
import { AttributeContainerItem, AttributeOptionList } from './attributeOptionList';

// Styled components

const VariantsSection = styled(Section)`
    height: calc(100vh - 310px);
    overflow-y: auto;
`;

const FormControlAttribute = styled(FormControl)`
    label {
        font-weight: bold;
    }
`;

const AttributeContainer = styled.div`
    display: grid;
    grid-template-columns: 30px 1fr 1fr 30px;
    grid-column-gap: 20px;
    align-items: top;
    border-bottom: 1px solid ${(props) => props.theme.app.primary_light};
    padding: 20px 0 10px;
    &:first-child {
        padding: 0 0 10px;
    }
`;

const LinkRemoveAttribute = styled(Link)`
    display: flex;
    align-self: center;
    ${Icon} {
        color: ${(props) => props.theme.app.primary};
        svg {
            height: 24px;
            width: 24px;
        }
    }
`;

const LinkAddButtonContainer = styled.div`
    display: flex;
    justify-content: center;
    font-weight: 700;
    font-size: 14px;
    margin-top: 25px;
    a {
        display: flex;
        align-items: center;
        text-decoration: none;
        color: ${(props) => props.theme.app.secondary};
        svg {
            margin-right: 5px;
        }
    }
`;

const VariantSummary = styled.div`
    ul {
        padding: 0 20px;
        list-style-type: decimal;

        > li {
            margin-bottom: 5px;
            label {
                &:after {
                    content: ' - ';
                }

                &:last-child:after {
                    content: '';
                }
            }
        }
    }
`;

const MovedIcon = styled(Icon)<{ movable?: boolean }>`
    vertical-align: middle;
    ${(props) =>
        props.movable &&
        !props.disabled &&
        `&:hover {
          cursor: grab;
          color: ${props.theme.app.secondary}
      }`}
`;

const PageHeaderContainer = styled.div`
    margin: 20px 0px !important;

    > div {
        padding-bottom: 0px !important;
    }
`;

const EditVariantsOffline: FunctionComponent<{ onSaveVariants: () => void }> = ({ onSaveVariants }) => {
    const { state, dispatch } = useProductConfigurationState();
    const { showDialog, closeDialog } = useDialogManager();
    const navigate = useNavigate();

    // Variants set in the product state
    const modelColorCodes = state.product.colors.map((c) => {
        return JSON.parse(c.code || '[]') as SelectedOptionValue[];
    });

    // State
    const [newModelColorCodes, setNewModelColorCodes] = useState<SelectedOptionValue[][]>(
        modelColorCodes[0].length > 0 ? [...modelColorCodes] : modelColorCodes.map((mc) => [...mc, { Id: uuid(), Label: '', Value: { Id: uuid(), Label: '' } }])
    );
    const [isFirstLoad, setIsFirstLoad] = useState(true);

    // Attributes
    const attributes = newModelColorCodes.length == 0 ? [] : newModelColorCodes[0];

    // Verifica se l'utente ha inserito qualcosa
    const hasUnsavedChanges = () => {
        const codes = newModelColorCodes.flat();
        return codes.some((o) => o.Label !== '' || o.Value.Label !== '');
    };

    const handleBackClick = () => {
        if (hasUnsavedChanges()) {
            showDialog(
                'unsaved-changes',
                <QuestionDialog
                    message={T._('You have unsaved changes. Are you sure you want to go back?', 'Admin')}
                    onYesClick={() => {
                        closeDialog('unsaved-changes');
                        navigate(-1);
                    }}
                />
            );
        } else {
            navigate(-1);
        }
    };

    // Mostra avviso di unsaved changes quando si tenta di lasciare la pagina
    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (hasUnsavedChanges()) {
                e.preventDefault();
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [newModelColorCodes]);

    // Callbacks

    // ****** Adding options
    const onAddOption = (attributeId: string, optionLabel: string) => {
        let list = [...newModelColorCodes];
        let attributes = [];

        if (newModelColorCodes[0].length == 1) list.push([{ Id: attributeId, Label: newModelColorCodes[0][0].Label, Value: { Id: uuid(), Label: '' } }]);
        else {
            for (let i = 0; i < newModelColorCodes[0].length; i++) {
                let elements: SelectedOptionValue[] = [...new Set(list.map((m) => JSON.stringify(m[i])))].map((m2) => JSON.parse(m2));

                if (newModelColorCodes[0][i].Id === attributeId)
                    elements = [...elements, { Id: attributeId, Label: newModelColorCodes[0][i].Label, Value: { Id: uuid(), Label: '' } }];

                attributes.push(elements);
            }
            list = cartesianProduct(...attributes);
        }
        setNewModelColorCodes(list);
    };

    // ****** Removing options
    const onRemoveOption = (attributeId: string, optionId: string, optionLabel: string) => {
        let list = [...newModelColorCodes];

        if (list.filter((mc) => mc.some((attr) => attr.Id == attributeId)).length <= 1) return;

        list = list.filter((mc) => !mc.some((attr) => attr.Id == attributeId && attr.Value.Id == optionId));
        if (list.length > 0) setNewModelColorCodes(list);

        let modelColors = [...state.product.colors];
        let newModelColorCodesStringify = list.map((mc) => JSON.stringify(mc));

        modelColors.forEach((c, i) => {
            if (!newModelColorCodesStringify.includes(c.code)) {
                let modelColor = state.product.colors.find((col) => col.code === c.code);
                if (modelColor) dispatch(removeColorAction(modelColor));
            }
        });
    };

    // ****** Edit options
    const onEditOption = (newValue: string, attributeId: string, optionId: string) => {
        let list = [...newModelColorCodes];
        list.forEach((mc) => {
            let attribute = mc.find((attr) => attr.Id === attributeId);
            if (attribute && attribute.Value.Id === optionId) attribute.Value.Label = newValue;
        });
        setNewModelColorCodes(list);

        let modelColorsCopy = [...state.product.colors];
        list.forEach((mc, i) => {
            // if (mc.some(o=>o.Label == '' || o.Value.Label == ''))
            //   return;

            let modelColor = modelColorsCopy.find((c) => {
                let modelColorCode = JSON.parse(c.code) as SelectedOptionValue[];
                return modelColorCode.some((o, i) => o.Id === mc[i].Id && o.Value.Id === mc[i].Value.Id);
            });

            if (modelColor) {
                let nameVariant = mc.map((o) => o.Value.Label).join(' - ');
                dispatch(editColorNameAndAttributesAction(modelColor, nameVariant, mc));
            } else {
                dispatch(addNewColorAction(mc));
            }

            modelColorsCopy = modelColorsCopy.filter((mc) => modelColor && mc.guid != modelColor.guid);
        });
    };

    // ****** Edit attribute
    const onEditAttribute = (newAttributeValue: string, attributeId: string) => {
        let list = [...newModelColorCodes];
        list.forEach((mc) => {
            let attribute = mc.find((attr) => attr.Id === attributeId);
            if (attribute) attribute.Label = newAttributeValue;
        });
        setNewModelColorCodes(list);

        let modelColorsCopy = [...state.product.colors];
        list.forEach((mc, i) => {
            // if (mc.some(o=>o.Label == '' || o.Value.Label == ''))
            //   return;

            let modelColor = modelColorsCopy.find((c) => {
                let modelColorCode = JSON.parse(c.code) as SelectedOptionValue[];
                return modelColorCode.some((o, i) => o.Id === mc[i].Id && o.Value.Id === mc[i].Value.Id);
            });

            if (modelColor) {
                let nameVariant = mc.map((o) => o.Value.Label).join(' - ');
                dispatch(editColorNameAndAttributesAction(modelColor, nameVariant, mc));
            } else {
                dispatch(addNewColorAction(mc));
            }

            modelColorsCopy = modelColorsCopy.filter((mc) => modelColor && mc.guid != modelColor.guid);
        });
    };

    // ****** Adding new attribute
    const onAddNewAttribute = () => {
        let list = [...newModelColorCodes];
        // if (list.some(attr=>attr.some(value => value.Label == '')))
        //   return;

        list = cartesianProduct(list, [{ Id: uuid(), Label: '', Value: { Id: uuid(), Label: '' } }]);
        setNewModelColorCodes(list);
    };

    // ***** Removing attribute
    const onRemoveAttribute = (attribute: SelectedOptionValue) => {
        if (newModelColorCodes[0].length == 0) return;

        let list = [...newModelColorCodes];
        let indexAttributeToRemove = attributes.findIndex((a) => a.Id === attribute.Id);

        if (indexAttributeToRemove >= 0) {
            list.forEach((mp) => mp.splice(indexAttributeToRemove, 1));

            list = [...new Set(list.map((m) => JSON.stringify(m)))].map((m2) => JSON.parse(m2));
            setNewModelColorCodes(list);

            let modelColorsCopy = [...state.product.colors];
            list.forEach((mc, i) => {
                let modelColor = modelColorsCopy.find((c) => {
                    let modelColorCode = (JSON.parse(c.code) || []) as SelectedOptionValue[];
                    if (modelColorCode.length == 0) return false;

                    if (modelColorCode.length != mc.length)
                        return modelColorCode.some((o, i) => {
                            if (i >= mc.length) return true;
                            return o.Id === mc[i].Id && o.Value.Id === mc[i].Value.Id;
                        });

                    return false;
                });

                // Aggiorno il model color
                if (modelColor) {
                    let nameVariant = mc.map((o) => o.Value.Label).join(' - ');
                    dispatch(editColorNameAndAttributesAction(modelColor, nameVariant, mc));
                    modelColorsCopy = modelColorsCopy.filter((mc) => modelColor && modelColor.guid != mc.guid);
                }
            });

            let newModelColorsState = [...state.product.colors];

            if (attribute.Label != '') {
                // Rimuovo quelli non più necessari
                modelColorsCopy.forEach((mc) => {
                    let modelColorCode = (JSON.parse(mc.code) || []) as SelectedOptionValue[];
                    if (modelColorCode.length >= list[0].length) {
                        let modelColorToRemove = newModelColorsState.find((c) => c.code === mc.code);

                        if (modelColorToRemove) dispatch(removeColorAction(modelColorToRemove));

                        newModelColorsState = newModelColorsState.filter((mc) => modelColorToRemove && mc.guid != modelColorToRemove.guid);
                    }
                });
            }
        }
    };

    // **** Confirm and save variants in the product state
    const onConfirmAndSaveVariants = () => {
        setIsFirstLoad(false);
        const codes = newModelColorCodes.flat();
        const hasErrors =
            newModelColorCodes[0][0].Label == '' ||
            codes.some((o) => o.Label == '' || !validateInputSecurity(o.Label).isValid || o.Value.Label == '' || !validateInputSecurity(o.Value.Label).isValid);

        if (hasErrors) {
            requestAnimationFrame(() => {
                const firstError = document.querySelector<HTMLElement>('span[data-has-error]');
                if (firstError) {
                    firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    const input =
                        (firstError.previousElementSibling as HTMLInputElement | null) ??
                        firstError.closest<HTMLElement>('div')?.querySelector<HTMLInputElement>('input');
                    input?.focus();
                }
            });
            return;
        }
        onSaveVariants();
    };

    const onChangeAttributeValue = debounce(onEditAttribute, 10);
    const handleInputAttributeChange = (e: React.ChangeEvent<HTMLInputElement>, attributeId: string) => {
        onChangeAttributeValue(e.currentTarget.value, attributeId);
    };

    // ** Body render component
    return (
        <ContainerBase>
            <PageHeaderContainer>
                <PageHeader isBackVisible={false} title={T._('Attribute setup', 'Admin')}></PageHeader>
            </PageHeaderContainer>
            <VariantsSection>
                {/* <DndContext>
      <SortableContext 
        strategy={verticalListSortingStrategy}
        items={attributes.map(m=>m.Id)}> */}
                {attributes.length > 0 &&
                    attributes.map((attribute, index) => {
                        return (
                            <AttributeContainerItem itemID={attribute.Id} key={`Attribute-${index}`}>
                                <FormControlAttribute label='Attribute name' caption={T._('Enter your attribute title, such as "color" or "size"', 'Admin')}>
                                    <InputWithValidation
                                        message={`${!validateInputSecurity(attribute.Label).isValid ? T._(`Invalid characters detected: ${validateInputSecurity(attribute.Label).invalidCharacters.join(', ')}`, 'Admin') : !isFirstLoad && validateRequired(attribute.Label, true) ? T._('Required', 'Admin') : ''}`}
                                        value={attribute.Label}
                                        onChange={(e) => handleInputAttributeChange(e, attribute.Id)}
                                        placeholder={T._('Attribute name', 'Admin')}
                                        validationKey={`AttributeValidation-${attribute.Id}-${index}`}
                                        validate={(value) => (!isFirstLoad && validateRequired(value, true)) || !validateInputSecurity(value).isValid}
                                    />
                                </FormControlAttribute>
                                <FormControlAttribute
                                    label={T._('Attribute values', 'Admin')}
                                    caption={T._('Enter the options for the attribute, such as "red" for "color" or "medium" for "size"', 'Admin')}
                                >
                                    <AttributeOptionList
                                        isFirstLoad={isFirstLoad}
                                        onRemoveOption={onRemoveOption}
                                        onEditOption={onEditOption}
                                        onAddOption={onAddOption}
                                        values={[...new Set(newModelColorCodes.map((m) => JSON.stringify(m[index])))].map((m2) => JSON.parse(m2))}
                                    />
                                </FormControlAttribute>
                                {newModelColorCodes[0].length > 1 && (
                                    <LinkRemoveAttribute onClick={() => onRemoveAttribute(attribute)}>
                                        <Icon>
                                            <TrashIcon />
                                        </Icon>
                                    </LinkRemoveAttribute>
                                )}
                            </AttributeContainerItem>
                        );
                    })}
                {/* </SortableContext>
      </DndContext> */}

                {/* Link add new attribute */}
                <LinkAddButtonContainer>
                    <Link onClick={onAddNewAttribute}>
                        <Icon>
                            <AiOutlinePlus></AiOutlinePlus>
                        </Icon>
                        {T._('Add new attribute', 'Admin')}
                    </Link>
                </LinkAddButtonContainer>

                <PageFooterButtons $fixed={true}>
                    <Button onClick={onConfirmAndSaveVariants}>{T._('Save', 'Admin')}</Button>
                </PageFooterButtons>
            </VariantsSection>
        </ContainerBase>
    );
};

export default EditVariantsOffline;
